import {
  COMPANY_NAME_MALFORMED,
  EMAIL_MISSING,
  IDENTIFIER_MALFORMED,
  MISSING_ADDRESS,
  MISSING_ADDRESS_PARTS,
  MISSING_COMPANY_NAME,
  MISSING_DATE_OF_BIRTH,
  MISSING_IDENTIFIER,
  MISSING_IDENTIFIER_QUALIFIER,
  PHONE_MISSING,
  QUALIFIER_MALFORMED,
  WRONG_IDENTIFIER_LENGTH,
} from '@constants/entity-validation-messages';
import { intersection } from 'lodash-es';
import { AddressModel, ValidationMessageModel } from '@data-layer/models/common';
import { EntityModel } from '@data-layer/models/entities';
import { UtilsHelper } from '@helpers/utils-helper';

export class EntityValidationHelper {
  public static getEntityValidationText(entity: EntityModel, fieldName: string) {
    const messages = this.getEntityErrorMessagesForField(fieldName);
    const allErrors = (entity?.validationMessages || []).map((v) => v.text);
    const fieldErrors = intersection(allErrors, messages);
    if (!fieldErrors.length) {
      return null;
    }

    return `BACK_ERROR.${fieldErrors[0]}`;
  }

  public static isValidEntityAddress(address: AddressModel): boolean {
    if (!address || !address.validationMessages) {
      return true;
    }
    const { validationMessages } = address;

    return !Object.keys(validationMessages).length;
  }

  private static getEntityErrorMessagesForField(fieldName: string) {
    let result = [];
    switch (fieldName) {
      case 'entityIdentifier':
        result = [WRONG_IDENTIFIER_LENGTH, MISSING_IDENTIFIER, IDENTIFIER_MALFORMED];
        break;

      case 'entityIdentifierQualifier':
        result = [MISSING_IDENTIFIER_QUALIFIER, QUALIFIER_MALFORMED];
        break;

      case 'name':
        result = [MISSING_COMPANY_NAME, COMPANY_NAME_MALFORMED];
        break;

      case 'address':
        result = [MISSING_ADDRESS, MISSING_ADDRESS_PARTS];
        break;

      case 'dateOfBirth':
        result = [MISSING_DATE_OF_BIRTH];
        break;

      case 'phone':
        result = [PHONE_MISSING];
        break;

      case 'email':
        result = [EMAIL_MISSING];
        break;

      default:
        break;
    }

    return result;
  }

  public static get entityMapValidation() {
    const map = new Map<string, (entity: EntityModel) => string[]>();

    map.set('name', this.entityNameValidationFc.bind(this));
    map.set('entityCode', this.entityCodeValidationFc.bind(this));
    map.set('entityIdentifier', this.entityIdentifierValidationFc.bind(this));
    map.set('entityIdentifierQualifier', this.entityIdentifierQualifierValidationFc.bind(this));
    map.set('dateOfBirth', this.entityDateOfBirthValidationFc.bind(this));
    map.set('addressId', this.entityAddressIdValidationFc.bind(this));
    map.set('address', this.entityAddressValidationFc.bind(this));
    map.set('phone', this.entityPhoneValidationFc.bind(this));
    map.set('email', this.entityEmailValidationFc.bind(this));
    map.set('fax', this.entityFaxValidationFc.bind(this));

    return map;
  }

  public static entityNameValidationFc(entity: EntityModel) {
    return this.findErrors(entity, [MISSING_COMPANY_NAME]);
  }

  public static entityCodeValidationFc(entity: EntityModel) {
    return [];
  }

  public static entityIdentifierValidationFc(entity: EntityModel) {
    return this.findErrors(entity, [MISSING_IDENTIFIER, WRONG_IDENTIFIER_LENGTH]);
  }

  public static entityIdentifierQualifierValidationFc(entity: EntityModel) {
    return this.findErrors(entity, [MISSING_IDENTIFIER_QUALIFIER]);
  }

  public static entityDateOfBirthValidationFc(entity: EntityModel) {
    return this.findErrors(entity, [MISSING_DATE_OF_BIRTH]);
  }

  public static entityAddressIdValidationFc(entity: EntityModel) {
    return this.findErrors(entity, [MISSING_ADDRESS, MISSING_ADDRESS_PARTS]);
  }

  public static entityAddressValidationFc(entity: EntityModel) {
    const messages: string[] = [];

    if (entity && entity.address && entity.address.validationMessages && Object.keys(entity.address.validationMessages).length) {
      const addressValidationMessages: any = entity.address.validationMessages;
      for (const field in addressValidationMessages) {
        if (addressValidationMessages.hasOwnProperty(field)) {
          messages.push(addressValidationMessages[field].text);
        }
      }
    }

    return UtilsHelper.uniq<string>([...messages, ...this.findErrors(entity, [MISSING_ADDRESS, MISSING_ADDRESS_PARTS])]);
  }

  public static entityPhoneValidationFc(entity: EntityModel) {
    return [];
  }

  public static entityEmailValidationFc(entity: EntityModel) {
    return [];
  }

  public static entityFaxValidationFc(entity: EntityModel) {
    return [];
  }

  private static findErrors(object: { validationMessages: ValidationMessageModel[]; [key: string]: any }, errors: string[]): string[] {
    const messages: string[] = [];

    for (const error of errors) {
      if ((object?.validationMessages || []).find((msg) => msg.text === error)) {
        messages.push(error);
      }
    }

    return UtilsHelper.uniq<string>(messages);
  }

  public static entityGetSummaryValidationText(entity: EntityModel, field: string): string {
    const mapValue = this.entityMapValidation;
    const validations = mapValue.has(field) ? mapValue.get(field)(entity) : [];
    if (!validations || validations?.length === 0) {
      return null;
    }

    return `BACK_ERROR.${validations[0]}`;
  }
}
